import { useState } from "react";
import { Mail, Phone, MapPin, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

export default function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    company: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    // MOCK: In frontend-only mode, simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    toast.success("Message sent!", {
      description: "We'll get back to you within 24 hours.",
    });
    
    setFormData({ name: "", email: "", phone: "", company: "", message: "" });
    setLoading(false);
  };

  return (
    <div data-testid="page-contact" className="py-16 md:py-20">
      <div className="max-w-[1480px] mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="overline">Get In Touch</div>
        <h1 className="mt-4 font-serif text-5xl md:text-7xl tracking-tight max-w-4xl">
          Let's discuss your requirements.
        </h1>
        <p className="mt-8 text-foreground/75 text-lg max-w-3xl leading-relaxed">
          Whether you need a formal quotation, product samples, or just have questions about 
          our products and services — we're here to help.
        </p>

        <div className="mt-20 grid grid-cols-1 lg:grid-cols-3 gap-10">
          {/* Contact Info */}
          <div className="lg:col-span-1 space-y-8">
            <div className="border border-border p-6 bg-[#0A0909]">
              <Mail size={24} className="text-[#3FD97A]" />
              <h3 className="mt-4 font-serif text-xl">Email</h3>
              <p className="mt-2 text-foreground/80 break-all">muhammadmubarak@qaafgroup.com</p>
              <p className="mt-1 text-foreground/80 break-all">rehmanrauf@qaafgroup.com</p>
              <p className="mt-1 text-sm text-foreground/60">Sales & general enquiries</p>
            </div>

            <div className="border border-border p-6 bg-[#0A0909]">
              <Phone size={24} className="text-[#3FD97A]" />
              <h3 className="mt-4 font-serif text-xl">Phone</h3>
              <p className="mt-2 text-foreground/80">+92 300 1234567</p>
              <p className="mt-1 text-sm text-foreground/60">WhatsApp available</p>
            </div>

            <div className="border border-border p-6 bg-[#0A0909]">
              <MapPin size={24} className="text-[#3FD97A]" />
              <h3 className="mt-4 font-serif text-xl">Office</h3>
              <p className="mt-2 text-foreground/80 leading-relaxed">
                ق Group (Pvt.) Ltd.<br />
                Punjab Agricultural Region<br />
                Pakistan
              </p>
            </div>

            <div className="border border-border p-6 bg-[#0A0909]">
              <Clock size={24} className="text-[#3FD97A]" />
              <h3 className="mt-4 font-serif text-xl">Business Hours</h3>
              <p className="mt-2 text-foreground/80">Mon-Fri: 9:00 AM - 6:00 PM PKT</p>
              <p className="mt-1 text-sm text-foreground/60">GMT+5</p>
            </div>
          </div>

          {/* Contact Form */}
          <div className="lg:col-span-2">
            <div className="border border-border p-10 bg-[#0A0909]">
              <h2 className="font-serif text-3xl mb-2">Send us a message</h2>
              <p className="text-sm text-foreground/70 mb-8">
                For formal RFQs (Request for Quotation), please use the{" "}
                <a href="/rfq" className="text-[#3FD97A] hover:underline">dedicated RFQ form</a>.
              </p>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name" className="font-mono text-[11px] uppercase tracking-[0.2em]">Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      required
                      className="mt-2 bg-[#141212] border-border"
                      data-testid="contact-name"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email" className="font-mono text-[11px] uppercase tracking-[0.2em]">Email *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="mt-2 bg-[#141212] border-border"
                      data-testid="contact-email"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="phone" className="font-mono text-[11px] uppercase tracking-[0.2em]">Phone</Label>
                    <Input
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className="mt-2 bg-[#141212] border-border"
                      data-testid="contact-phone"
                    />
                  </div>
                  <div>
                    <Label htmlFor="company" className="font-mono text-[11px] uppercase tracking-[0.2em]">Company</Label>
                    <Input
                      id="company"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      className="mt-2 bg-[#141212] border-border"
                      data-testid="contact-company"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="message" className="font-mono text-[11px] uppercase tracking-[0.2em]">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows={6}
                    className="mt-2 bg-[#141212] border-border resize-none"
                    data-testid="contact-message"
                  />
                </div>

                <Button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-submit"
                  className="bg-[#3FD97A] hover:bg-[#4FE98A] text-[#0A0909] font-mono text-[11px] uppercase tracking-[0.22em] px-8 py-6 font-semibold"
                >
                  {loading ? "Sending..." : "Send Message"}
                </Button>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
